import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRightIcon, XIcon } from 'lucide-react';
import { clients } from '../../../data/clients';
interface ClientDirectoryProps {
  isOpen: boolean;
  reduceMotion: boolean;
  onClose: () => void;
  onOpenProject: (projectId: string) => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function ClientDirectory({
  isOpen,
  reduceMotion,
  onClose,
  onOpenProject
}: ClientDirectoryProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-138" data-mp="3vhm1t2qde6ef">
      {isOpen && <motion.div className="fixed inset-0 z-[60] flex items-end justify-center md:items-center md:p-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: reduceMotion ? 0 : 0.2,
      ease: EASE
    }} data-id="element-139" data-mp="3vinetcqy52ys">
          <button type="button" aria-label="Close client directory" className="absolute inset-0 cursor-default" style={{
        background: 'rgba(23, 23, 22, 0.45)'
      }} onClick={onClose} tabIndex={-1} data-id="element-140" data-mp="3gpjyf8p3cv9n" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="client-directory-title" className="relative flex max-h-[92vh] w-full max-w-[1280px] flex-col overflow-hidden rounded-t-2xl md:rounded-2xl" style={{
        background: 'var(--section-bg)'
      }} initial={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} transition={{
        duration: reduceMotion ? 0 : 0.26,
        ease: EASE
      }} data-id="element-141" data-mp="8a9xi3wxtw3g">
            <div className="flex flex-shrink-0 items-center justify-between gap-4 px-6 py-5 md:px-12 md:py-7" style={{
          borderBottom: '1px solid var(--border)'
        }} data-id="element-142" data-mp="1665gutdxk0k3">
              <div data-id="element-143" data-mp="wehx7enm7nt6">
                <h3 id="client-directory-title" className="text-2xl tracking-tight md:text-4xl" style={{
              color: 'var(--fg)'
            }} data-id="element-144" data-mp="wdzsruqsu3a6">
                  Client directory
                </h3>
                <p className="font-aspekta mt-1 text-sm" style={{
              color: 'var(--muted)'
            }} data-id="element-145" data-mp="nfxjkrwww4bt">
                  {clients.length} teams across 6 countries
                </p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full transition-colors duration-150 ease-out" style={{
            border: '1px solid var(--border)',
            color: 'var(--fg)',
            background: 'var(--card)'
          }} aria-label="Close" data-id="element-146" data-mp="dgf4opvuz8qm">
                <XIcon size={18} data-id="element-147" data-mp="8syu32hm7jzq" />
              </button>
            </div>

            <div className="overflow-y-auto overscroll-contain px-6 py-10 md:px-12 md:py-14" data-id="element-148" data-mp="3sta88hl71s7">
              <ul className="grid grid-cols-2 gap-x-5 gap-y-10 sm:gap-x-6 sm:gap-y-12 sm:grid-cols-3 lg:grid-cols-5 lg:gap-x-10 lg:gap-y-14" data-id="element-149" data-mp="hsc9ebsf72sd">
                {clients.map(client => <li key={client.name} className="flex flex-col" data-id="element-150" data-mp="riduqvte273l">
                    <div className="flex h-12 items-center" data-id="element-151" data-mp="rkrlwtky6z5v">
                      {client.logo ? <img src={client.logo} alt={client.name} loading="lazy" className="max-w-full" style={{
                  maxHeight: 34,
                  width: 'auto',
                  objectFit: 'contain',
                  filter: 'brightness(0)'
                }} data-id="element-152" data-mp="mlwvb5gqo6b7" /> : <span className="font-aspekta text-xl leading-tight tracking-tight sm:text-2xl sm:leading-none" style={{
                  color: 'var(--fg)'
                }} data-id="element-153" data-mp="2uxzcs7wlsok7">
                          {client.name}
                        </span>}
                    </div>
                    <div className="font-aspekta mt-4 space-y-0.5 text-sm leading-snug" style={{
                color: 'var(--muted)'
              }} data-id="element-154" data-mp="272eeg1m4madv">
                      <p data-id="element-155" data-mp="2c17vg40mgjis">{client.location}</p>
                      <p data-id="element-156" data-mp="2h02d32mus21b">{client.industry}</p>
                      <p data-id="element-157" data-mp="2gyzpb3ktufpm">{client.format}</p>
                    </div>
                    {client.projectId && <button type="button" onClick={() => client.projectId && onOpenProject(client.projectId)} className="font-aspekta mt-3 inline-flex items-center gap-1 self-start bg-transparent text-sm transition-opacity duration-150 ease-out hover:opacity-70" style={{
                color: 'var(--accent)'
              }} data-id="element-158" data-mp="2m06uqvzhz2t4">
                        View project
                        <ArrowUpRightIcon size={14} aria-hidden="true" data-id="element-159" data-mp="1c044zokp1h73" />
                      </button>}
                  </li>)}
              </ul>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}